<script setup>
import PrematchSportsNavigation from '@/components/PrematchSportsNavigation.vue';
import MainEvents from './Partials/MainEvents.vue';
import MainLeagues from './Partials/MainLeagues.vue';
import Carousel from '@/components/App/Carousel.vue';
import MainEventCard from './Partials/MainEventCard.vue';
import Spinner from '@/components/Spinner.vue';
import { storeToRefs } from 'pinia';
import { onMounted, ref, watch, onUnmounted } from 'vue';
import { useFixturesStore } from '@/stores/fixtures';

const fixtures = useFixturesStore()
const opening = ref(false)
const { upcomingEvents , tenEvents } = storeToRefs(fixtures)
const { getMainFixtures } = storeToRefs(fixtures)
const liveSelectionsMap = ref({})
const preSelectionsMap = ref({})
const visibleTenEvents = ref(new Set())
const visibleUpcomingEvents = ref(new Set())
const liveScoreboardsMap = ref({})

let observer

const observeEvents = () => {
  if (observer) observer.disconnect()

  observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        const eventId = Number(entry.target.dataset.i)
        if (entry.isIntersecting) {
          if (entry.target.classList.contains("ten-event-item")) {
            visibleTenEvents.value.add(eventId)
          }
          if (entry.target.classList.contains("upcoming-event-item")) {
            visibleUpcomingEvents.value.add(eventId)
          }
        } else {
          visibleTenEvents.value.delete(eventId)
          visibleUpcomingEvents.value.delete(eventId)
        }
      })
    },
    { root: null, threshold: 0.1 }
  )

  document.querySelectorAll(".ten-event-item, .upcoming-event-item").forEach((el) => {
    observer.observe(el)
  })
}

onMounted(() => {
  fixtures.fetchMainCompetitions()
  fixtures.fetchMainFixtures()
  setTimeout(() => observeEvents(), 100)
})

onUnmounted(() => {
  if (observer) observer.disconnect()
})

watch(
  visibleTenEvents,
  async (ids) => {
    if (ids.size === 0) return
    const res = await fixtures.fetchSelections(Array.from(ids), "live", [20889])
    for (const ev of res) {
      liveSelectionsMap.value[ev.fi] = ev.m || []
    }
    await fixtures.fetchScoreboards(Array.from(ids)).then(res => {
      if (Array.isArray(res)) {
        res.forEach(sb => {
          liveScoreboardsMap.value[sb.fi] = sb
        })
      }
    })
  },
  { deep: true }
)
watch(
  visibleUpcomingEvents,
  async (ids) => {
    if (ids.size === 0) return
    const res = await fixtures.fetchSelections(Array.from(ids), "pre", [20890])
    for (const ev of res) {
      preSelectionsMap.value[ev.fi] = ev.m || []
    }
  },
  { deep: true }
)
</script>

<template>
  <div class="xl:flex flex-1 overflow-hidden">
    <div class="flex-1 w-full">
      <PrematchSportsNavigation class="py-2.5"/>
      <div class="pt-1.5 px-3" v-if="getMainFixtures(1).length">
        <Carousel
          height="250px"
          :items="getMainFixtures(1)"
          :slidesPerView="1"
          :spaceBetween="20"
          :showNavigation="true"
          :showPagination="true"
          :autoplay="false"
          :swiperOptions="{ loop: true }"
        >
          <template #slide="{ slide }">
            <MainEventCard :event="slide" />
          </template>
        </Carousel>
      </div>
      <MainLeagues />
      <MainEvents
        :title="$t('partials.17')"
        :sport="1"
        :market="20889"
        :events="tenEvents(1)"
        :inline="false"
        class="mt-4 px-3 py-1.5"
        :selectionsMap="liveSelectionsMap"
        :scoreboardsMap="liveScoreboardsMap"
        :live="true"
        item-class="ten-event-item"
      />
      <MainEvents
        :title="$t('partials.18')"
        :sport="1"
        :market="20890"
        :events="upcomingEvents(1)"
        :inline="false"
        class="mt-4 px-3 py-1.5"
        :selectionsMap="preSelectionsMap"
        :live="false"
        item-class="upcoming-event-item"
      />
    </div>
    <Spinner :show="opening" />
  </div>
</template>

<style scoped>
.show-btn {
    @apply text-center flex items-center justify-center gap-1 text-[var(--color-primary4)] hover:text-[var(--color-primary4)] text-sm font-bold
}
.mgc-Chevron {
    mask-image: url('/chevron-right.svg');
    background-color: var(--color-primary4);
    mask-size: 12px 12px;
    width: 12px;
    height: 12px;
}
.mgc-Chevron:hover {
    background-color: var(--color-primary4);
}
.star-icn{
    mask-image: url('/star.svg');
    background-color: var(--color-primary4);
    mask-size: 18px 18px;
    width: 18px;
    height: 18px;  
}
.slide-img{
    object-fit: fill;
    height: 100%;
    width: 100%;
}
.side-cont{
    position: relative;
    width: 100%;
    height: 250px;
    min-height: 250px;
    border: 1px solid var(--banner-border);
    border-radius: 6px;
    overflow: hidden;
    box-shadow: 0 3px 10px #0006;
    margin-bottom: 30px;   
}
</style>
